import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';

import {
  selectCurrentUser,
  selectSessionRestoring,
} from './reducers/authSlice';
import { selectHomeSearch } from './reducers/uiReducer';
import type { RootState, AppDispatch } from './store';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useAuth = () => {
  const user = useSelector(selectCurrentUser);
  const restoring = useSelector(selectSessionRestoring);

  // auth.user is null on every page load until App restores it from the session
  // cookie, so "no user" alone does not mean signed out. The flag is decided
  // once, when the store is built, from the same cookie App bootstraps off —
  // and cleared when that restore settles either way. It deliberately does not
  // re-read the cookie later: a token that appears mid-session has no restore
  // behind it to clear the flag, and would suppress the login prompt for good.
  const sessionRestoring = !user && restoring;

  return useMemo(
    () => ({ user, sessionRestoring, signedOut: !user && !sessionRestoring }),
    [user, sessionRestoring]
  );
};

export const useHomeSearch = () => {
  const homeSearch = useSelector(selectHomeSearch);
  return useMemo(() => ({ homeSearch }), [homeSearch]);
};

export const useOnScreen = (ref: any, rootMargin = '0px') => {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
      }
    );
    let currentRef = ref?.current;
    if (ref?.current) {
      observer.observe(ref.current);
      currentRef = ref.current;
    }
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, rootMargin]); // Empty array ensures that effect is only run on mount and unmount
  return isIntersecting;
};
